import React, { useState, useEffect, useMemo } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, getDocs, limit, orderBy } from 'firebase/firestore';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Percent, TrendingUp, XCircle, ShoppingCart, Info, ExternalLink, ChevronRight } from 'lucide-react';
import FallbackImage from '../components/FallbackImage';
import { useTheme } from '../contexts/ThemeContext';
import { Button, Card, StatCard } from '../components/ui';
import { auth, db } from '../firebase';

const appId = 'gift-card-manager-v1';

const cryptoHelpers = {
    async decrypt(ciphertextB64, key) {
        try {
            const encryptedBytes = new Uint8Array(atob(ciphertextB64).split('').map(c => c.charCodeAt(0)));
            const iv = encryptedBytes.slice(0, 12);
            const data = encryptedBytes.slice(12);
            const decryptedContent = await window.crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, data);
            return new TextDecoder().decode(decryptedContent);
        } catch (e) {
            console.error("Decryption failed:", e);
            return null;
        }
    }
};

const LoadingSpinner = ({ text }) => (
    <div className="flex flex-col justify-center items-center p-20 text-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-brand-600"></div>
        <p className="mt-4 text-slate-600 dark:text-slate-300">{text}</p>
    </div>
);

export default function RetailerPage({ slug, navigate }) {
    const [retailer, setRetailer] = useState(null);
    const [availableDeals, setAvailableDeals] = useState([]);
    const [allHistory, setAllHistory] = useState([]);
    const [similarRetailers, setSimilarRetailers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [minDiscount, setMinDiscount] = useState(0);
    const [minValue, setMinValue] = useState(0);
    const [sortOption, setSortOption] = useState('discount-desc');
    const [currentUser, setCurrentUser] = useState(null);
    const [userCards, setUserCards] = useState([]);
    const [cryptoKey, setCryptoKey] = useState(null);

    const { theme } = useTheme();
    const chartColors = useMemo(() => {
        const isDark = theme === 'dark';
        return {
            grid: isDark ? '#334155' : '#E2E8F0',
            axis: isDark ? '#94A3B8' : '#64748B',
            tooltip: {
                backgroundColor: isDark ? '#1E293B' : '#FFFFFF',
                borderColor: isDark ? '#334155' : '#E2E8F0',
                color: isDark ? '#F1F5F9' : '#0F172A',
                borderRadius: '0.75rem',
            },
        };
    }, [theme]);

    // Auth listener
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => setCurrentUser(user));
        return () => unsubscribe();
    }, []);

    // Session key
    useEffect(() => {
        const checkSession = async () => {
            try {
                const sessionKey = sessionStorage.getItem('sessionCryptoKey');
                if (sessionKey && currentUser) {
                    const key = await window.crypto.subtle.importKey('jwk', JSON.parse(sessionKey), { name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
                    setCryptoKey(key);
                } else {
                    setCryptoKey(null);
                }
            } catch (e) {
                console.error("Failed to restore session key", e);
                sessionStorage.removeItem('sessionCryptoKey');
            }
        };
        checkSession();
    }, [currentUser]);

    // Fetch page data
    useEffect(() => {
        const fetchPageData = async () => {
            if (!slug) { setError("No retailer specified."); setIsLoading(false); return; }
            setIsLoading(true);
            setSimilarRetailers([]);
            try {
                const retailersRef = collection(db, 'retailers');
                const q = query(retailersRef, where("slug", "==", slug), limit(1));
                const querySnapshot = await getDocs(q);
                if (querySnapshot.empty) { setError('Retailer not found.'); setIsLoading(false); return; }

                const retailerData = querySnapshot.docs[0].data();
                setRetailer(retailerData);
                const retailerId = retailerData.internalId;

                const cardBearId = retailerData.sourceIds?.cardBearId;
                const dealPromises = [
                    !cardBearId ? Promise.resolve(null) :
                        fetch(`/api/cardbear-proxy?storeId=${cardBearId}`)
                            .then(res => res.ok ? res.json() : null)
                            .then(data => {
                                const deal = data?.discounts?.[0];
                                if (deal && parseFloat(deal.highestDiscount) > 0) {
                                    return {
                                        id: `live-${retailerId}`,
                                        discount: parseFloat(deal.highestDiscount),
                                        value: 100,
                                        price: 100 * (1 - parseFloat(deal.highestDiscount) / 100),
                                        source: 'CardBear (Live)',
                                        url: `https://www.cardbear.com/gift-card-discount/${slug}`
                                    };
                                }
                                return null;
                            }).catch(() => null),
                    getDocs(query(collection(db, 'dealData'), where("retailerId", "==", retailerId)))
                        .then(snapshot => snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })))
                ];

                const [liveCardBearDeal, storedDeals] = await Promise.all(dealPromises);
                setAvailableDeals([...(liveCardBearDeal ? [liveCardBearDeal] : []), ...storedDeals]);

                const historyRef = collection(db, 'dealHistory');
                const historyQuery = query(historyRef, where("retailerId", "==", retailerId), orderBy("date", "desc"), limit(30));
                const historySnapshot = await getDocs(historyQuery);
                setAllHistory(historySnapshot.docs.map(doc => doc.data()).reverse());
            } catch (err) {
                console.error("Error fetching page data:", err);
                setError("Failed to load retailer data.");
            } finally {
                setIsLoading(false);
            }
        };
        fetchPageData();
    }, [slug]);

    // User cards
    useEffect(() => {
        if (!currentUser || !retailer || !cryptoKey) { setUserCards([]); return; }
        const fetchUserCards = async () => {
            const collectionPath = `users/${currentUser.uid}/apps/${appId}/gift_cards`;
            const userCardsQuery = query(collection(db, collectionPath), where("retailer_lowercase", "==", retailer.name.toLowerCase()));
            const snapshot = await getDocs(userCardsQuery);
            const decrypted = await Promise.all(snapshot.docs.map(async (doc) => {
                const card = { id: doc.id, ...doc.data() };
                const decryptedCode = card.code ? await cryptoHelpers.decrypt(card.code, cryptoKey) : '';
                return { ...card, code: decryptedCode, balance: card.initialValue - card.spent };
            }));
            setUserCards(decrypted);
        };
        fetchUserCards();
    }, [currentUser, retailer, cryptoKey]);

    // Similar retailers
    useEffect(() => {
        if (!retailer) return;
        const fetchSimilar = async () => {
            try {
                const retailersRef = collection(db, 'retailers');
                const q = query(retailersRef, where("category", "==", retailer.category), where("internalId", "!=", retailer.internalId), limit(6));
                const snapshot = await getDocs(q);
                setSimilarRetailers(snapshot.docs.map(doc => doc.data()));
            } catch (err) {
                console.error("Error fetching similar retailers:", err);
            }
        };
        fetchSimilar();
    }, [retailer]);

    const displayedDeals = useMemo(() => {
        let processedDeals = availableDeals.filter(deal =>
            deal.discount >= Number(minDiscount) &&
            (Number(minValue) === 0 || (deal.value && deal.value >= Number(minValue)))
        );
        processedDeals.sort((a, b) => {
            switch (sortOption) {
                case 'discount-desc': return b.discount - a.discount;
                case 'discount-asc': return a.discount - b.discount;
                case 'value-desc': return b.value - a.value;
                case 'value-asc': return a.value - b.value;
                case 'price-desc': return b.price - a.price;
                case 'price-asc': return a.price - b.price;
                default: return b.discount - a.discount;
            }
        });
        return processedDeals;
    }, [availableDeals, minDiscount, minValue, sortOption]);

    const stats = useMemo(() => {
        const bestDiscount = availableDeals.length > 0 ? Math.max(...availableDeals.map(d => d.discount)) : 0;
        const averageDiscount = allHistory.length > 0 ? allHistory.reduce((acc, day) => {
            const bestForDay = Object.values(day.dailyBestsByValue).reduce((max, d) => Math.max(max, d), 0);
            return acc + bestForDay;
        }, 0) / allHistory.length : 0;
        const advice = bestDiscount > averageDiscount * 1.05 ? 'BUY' : 'WAIT';
        return { averageDiscount, bestDiscount, advice };
    }, [availableDeals, allHistory]);

    const displayedHistory = useMemo(() => {
        return allHistory.map(day => {
            const bestsByValue = day.dailyBestsByValue;
            const bestDiscountForDay = Object.entries(bestsByValue)
                .filter(([value]) => Number(value) >= Number(minValue))
                .reduce((max, [, discount]) => Math.max(max, discount), 0);
            return {
                date: new Date(day.date).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' }),
                discount: bestDiscountForDay
            };
        });
    }, [allHistory, minValue]);


    if (isLoading) return <LoadingSpinner text="Loading Retailer..." />;
    if (error) return <div className="text-center p-8 text-red-500">{error}</div>;

    const selectClasses = "w-full p-2.5 border border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-xl text-sm focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none transition-all";

    return (
        <div className="bg-slate-50 dark:bg-slate-900 min-h-screen transition-colors duration-200">
            {/* Breadcrumb */}
            <div className="container mx-auto px-4 sm:px-6 pt-4">
                <nav className="flex items-center text-sm text-slate-500 dark:text-slate-400 space-x-1">
                    <a href="/home" onClick={(e) => { e.preventDefault(); navigate('/home'); }} className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors">Home</a>
                    <ChevronRight size={14} />
                    <a href="/deals" onClick={(e) => { e.preventDefault(); navigate('/deals'); }} className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors">Deals</a>
                    <ChevronRight size={14} />
                    <span className="text-slate-800 dark:text-white font-medium">{retailer.name}</span>
                </nav>
            </div>

            <div className="container mx-auto p-4 sm:p-6 lg:p-8">
                {/* Retailer header */}
                <div className="flex flex-col items-center justify-center mb-8 text-center">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl p-4 shadow-card mb-4 border border-slate-200 dark:border-slate-700">
                        <FallbackImage srcs={retailer.logoUrls} alt={`${retailer.name} logo`} className="h-16" />
                    </div>
                    <h1 className="text-3xl md:text-4xl font-extrabold text-slate-800 dark:text-white tracking-tight">{retailer.name} Gift Card Deals</h1>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Sidebar */}
                    <div className="lg:col-span-1 space-y-6">
                        <StatCard title="Best Current Discount" value={`${stats.bestDiscount.toFixed(2)}%`} icon={Percent} tone="brand" />
                        <StatCard title="30-Day Average" value={`${stats.averageDiscount.toFixed(2)}%`} icon={TrendingUp} tone="gray" />

                        {/* Scout's advice */}
                        <Card className={`flex items-center ${stats.advice === 'BUY'
                            ? 'bg-brand-50 dark:bg-brand-900/20 border-brand-200 dark:border-brand-800'
                            : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                            }`}>
                            <Info className={`h-8 w-8 mr-4 flex-shrink-0 ${stats.advice === 'BUY' ? 'text-brand-700 dark:text-brand-400' : 'text-amber-600 dark:text-amber-400'}`} />
                            <div>
                                <p className={`font-bold text-lg ${stats.advice === 'BUY' ? 'text-brand-800 dark:text-brand-200' : 'text-amber-800 dark:text-amber-200'}`}>Scout's Advice: {stats.advice}</p>
                                <p className={`text-sm ${stats.advice === 'BUY' ? 'text-brand-700 dark:text-brand-300' : 'text-amber-700 dark:text-amber-300'}`}>
                                    {stats.advice === 'BUY' ? 'Discounts are better than average right now.' : 'Deals are average; better savings may appear.'}
                                </p>
                            </div>
                        </Card>

                        <ManageCard retailer={retailer} user={currentUser} userCards={userCards} navigate={navigate} />

                        {/* History chart */}
                        <Card>
                            <h3 className="font-bold text-lg mb-3 text-slate-800 dark:text-white">30-Day Discount History</h3>
                            <div style={{ width: '100%', height: 200 }}>
                                <ResponsiveContainer>
                                    {displayedHistory.length > 0 ? (
                                        <LineChart data={displayedHistory} margin={{ top: 5, right: 20, left: -15, bottom: 5 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                                            <XAxis dataKey="date" fontSize={12} stroke={chartColors.axis} tick={{ fill: chartColors.axis }} />
                                            <YAxis unit="%" domain={['dataMin - 2', 'dataMax + 2']} fontSize={12} stroke={chartColors.axis} tick={{ fill: chartColors.axis }} />
                                            <Tooltip formatter={(value) => `${value.toFixed(2)}%`} contentStyle={chartColors.tooltip} cursor={{ stroke: chartColors.axis }} />
                                            <Line type="monotone" dataKey="discount" stroke="#16a34a" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />
                                        </LineChart>
                                    ) : (
                                        <div className="flex items-center justify-center h-full text-slate-500">
                                            <p>No history available for this filter.</p>
                                        </div>
                                    )}
                                </ResponsiveContainer>
                            </div>
                        </Card>
                    </div>

                    {/* Main content */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Filters */}
                        <Card>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                                <div>
                                    <label htmlFor="min-discount" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Min Discount</label>
                                    <input type="range" id="min-discount" min="0" max="50" step="1" value={minDiscount} onChange={(e) => setMinDiscount(e.target.value)} className="w-full h-2 bg-slate-200 dark:bg-slate-600 rounded-lg appearance-none cursor-pointer accent-brand-600" />
                                    <div className="text-center font-bold text-brand-700 dark:text-brand-400 mt-1 tabular-nums">{minDiscount}%</div>
                                </div>
                                <div>
                                    <label htmlFor="min-value" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Min Card Value</label>
                                    <select id="min-value" value={minValue} onChange={(e) => setMinValue(e.target.value)} className={selectClasses}>
                                        <option value="0">Any Value</option><option value="25">$25+</option><option value="50">$50+</option><option value="100">$100+</option><option value="200">$200+</option><option value="500">$500+</option>
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="sort-by" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Sort By</label>
                                    <select id="sort-by" value={sortOption} onChange={(e) => setSortOption(e.target.value)} className={selectClasses}>
                                        <option value="discount-desc">Discount: High to Low</option>
                                        <option value="discount-asc">Discount: Low to High</option>
                                        <option value="value-desc">Value: High to Low</option>
                                        <option value="value-asc">Value: Low to High</option>
                                        <option value="price-desc">Cost: High to Low</option>
                                        <option value="price-asc">Cost: Low to High</option>
                                    </select>
                                </div>
                                <Button variant="secondary" icon={XCircle} className="h-10" onClick={() => { setMinValue(0); setMinDiscount(0); setSortOption('discount-desc'); }}>Reset</Button>
                            </div>
                        </Card>

                        {/* Deal listings */}
                        {displayedDeals.length > 0 ? (
                            <div className="space-y-3">
                                {displayedDeals.map(deal => <ListingCard key={deal.id} deal={deal} />)}
                            </div>
                        ) : (
                            <Card className="text-center p-12">
                                <h3 className="text-xl font-semibold text-slate-700 dark:text-slate-200">No Deals Found</h3>
                                <p className="text-slate-500 dark:text-slate-400 mt-2">Try adjusting your filters or check back later!</p>
                            </Card>
                        )}
                    </div>
                </div>

                {similarRetailers.length > 0 && <SimilarRetailers retailers={similarRetailers} navigate={navigate} />}
            </div>
        </div>
    );
}

// ─── Sub-components ───

const ListingCard = ({ deal }) => {
    const finalPrice = deal.value ? deal.price : null;
    return (
        <Card hover className="flex items-center justify-between">
            <div className="flex items-center">
                <div className="text-center w-20">
                    <p className="font-bold text-xl text-brand-700 dark:text-brand-400 tabular-nums">{deal.discount.toFixed(2)}%</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">Discount</p>
                </div>
                <div className="ml-4 border-l border-slate-200 dark:border-slate-700 pl-4">
                    <p className="font-semibold text-slate-800 dark:text-white">{deal.value ? `$${deal.value.toFixed(2)} Gift Card` : `${deal.retailerName} Gift Card`}</p>
                    {finalPrice && <p className="text-sm text-slate-600 dark:text-slate-300">Pay only <span className="font-bold tabular-nums">${finalPrice.toFixed(2)}</span></p>}
                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Source: {deal.source}</p>
                </div>
            </div>
            <Button as="a" href={deal.url} target="_blank" rel="noopener noreferrer" pill icon={ShoppingCart} className="flex-shrink-0">Go to Deal</Button>
        </Card>
    );
};

const SimilarRetailers = ({ retailers, navigate }) => {
    const handleLinkClick = (e, path) => {
        e.preventDefault();
        if (navigate) navigate(path);
    };
    return (
        <div className="mt-12">
            <h2 className="text-2xl font-bold text-center text-slate-800 dark:text-white mb-6">Similar Gift Cards</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {retailers.map(retailer => (
                    <Card as="a" hover key={retailer.internalId} href={`/retailer/${retailer.slug}`} onClick={(e) => handleLinkClick(e, `/retailer/${retailer.slug}`)} className="flex flex-col items-center justify-center text-center">
                        <FallbackImage srcs={retailer.logoUrls} alt={`${retailer.name} logo`} className="h-12 object-contain mb-3" />
                        <p className="font-semibold text-sm text-slate-700 dark:text-slate-200">{retailer.name}</p>
                    </Card>
                ))}
            </div>
        </div>
    );
};

const ManageCard = ({ retailer, user, userCards, navigate }) => {
    if (!retailer) return null;
    const handleNavigate = (e, path) => { e.preventDefault(); if (navigate) navigate(path); };

    return (
        <Card>
            <h3 className="font-bold text-lg mb-3 text-slate-800 dark:text-white">Manage Your Card</h3>
            {user ? (
                <div className="space-y-3">
                    <div>
                        <p className="text-sm text-slate-600 dark:text-slate-300">Your Current Balances:</p>
                        {userCards && userCards.length > 0 ? (
                            <ul className="mt-2 space-y-2">
                                {userCards.map(card => (
                                    <li key={card.id} className="flex justify-between items-center bg-slate-50 dark:bg-slate-700 p-2.5 rounded-lg">
                                        <span className="font-mono text-sm text-slate-700 dark:text-slate-200">Card ending in ...{card.code?.slice(-4)}</span>
                                        <span className="font-bold text-slate-800 dark:text-white tabular-nums">${card.balance.toFixed(2)}</span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">You don't have any saved {retailer.name} cards.</p>
                        )}
                    </div>
                    <a href={`https://www.google.com/search?q=${retailer.name}+gift+card+balance`} target="_blank" rel="noopener noreferrer" className="text-brand-700 dark:text-brand-400 hover:underline flex items-center pt-2 border-t border-slate-200 dark:border-slate-700 text-sm font-medium">
                        <ExternalLink size={14} className="mr-1.5" />
                        Verify balances with {retailer.name}
                    </a>
                </div>
            ) : (
                <div className="space-y-3 text-center">
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                        <button onClick={(e) => handleNavigate(e, '/tool')} className="font-bold text-brand-700 dark:text-brand-400 hover:underline bg-transparent border-none p-0 cursor-pointer">Sign in</button> to track your balance for {retailer.name}.
                    </p>
                    <a href={`https://www.google.com/search?q=${retailer.name}+gift+card+balance`} target="_blank" rel="noopener noreferrer" className="text-brand-700 dark:text-brand-400 hover:underline flex items-center justify-center pt-3 border-t border-slate-200 dark:border-slate-700 text-sm font-medium">
                        <ExternalLink size={14} className="mr-1.5" />
                        Check Gift Card Balance
                    </a>
                </div>
            )}
        </Card>
    );
};
